import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DoctorFacade } from '@modules/doctor/doctor.facade';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { Doctor } from '@shared/models/doctor';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { LicenseReadOnlyComponent } from '../../components/license-read-only/license-read-only.component';
import { LicenseInputComponent } from '../../components/license-input/license-input.component';
import { NgIf, AsyncPipe } from '@angular/common';
import { DoctorNameComponent } from '../../components/doctor-name/doctor-name.component';
import { FlexModule } from '@angular/flex-layout/flex';
import { debounceDelay } from '@shared/models/consts';

@Component({
	selector: 'rx-doctor',
	templateUrl: './doctor.component.html',
	styleUrls: ['./doctor.component.scss'],
	providers: [DoctorFacade],
	standalone: true,
	imports: [
		FlexModule,
		DoctorNameComponent,
		NgIf,
		LicenseInputComponent,
		FormsModule,
		ReactiveFormsModule,
		LicenseReadOnlyComponent,
		AsyncPipe
	]
})
export class DoctorComponent extends BaseDestroyableComponent implements OnInit {
	doctor$: Observable<Doctor> = this.facade.doctor$;
	license$: Observable<string | undefined> = this.facade.doctor$.pipe(map(doctor => doctor?.Licence));
	isLicenseEditable$: Observable<boolean> = this.facade.isLicenseEditable$.pipe(
		withLatestFrom(this.facade.isReadOnly$),
		map(([isLicenseEditable, isReadOnly]) => {
			return !isReadOnly && isLicenseEditable;
		})
	);
	isReadOnly$: Observable<boolean> = this.facade.isReadOnly$;

	licenseFormControl = new FormControl<string | null>(null);

	constructor(private facade: DoctorFacade) {
		super();
	}

	ngOnInit(): void {
		this.getDoctorInformation();
		this.subscribeToUserChangesLicense();
		this.subscribeToStoreChanges();
	}

	private getDoctorInformation(): void {
		this.facade
			.getDoctorInformation()
			.pipe(
				switchMap(doctorInfo => this.afterRxValidForSave(doctorInfo)),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private afterRxValidForSave(doctorInfo: Doctor): Observable<boolean> {
		const isDoctorInfoValid = !!(doctorInfo?.Id && doctorInfo?.Licence && doctorInfo?.Name);

		return this.facade.isRxValidForSave$.pipe(
			filter((isRxValidForSave): isRxValidForSave is boolean => typeof isRxValidForSave === 'boolean'),
			tap(isRxValidForSave => {
				if (isRxValidForSave === false && !this.licenseFormControl.value && isDoctorInfoValid) {
					this.licenseFormControl.setErrors({ required: true });
					this.licenseFormControl.markAsTouched();
				}
			})
		);
	}

	private subscribeToUserChangesLicense(): void {
		this.licenseFormControl.valueChanges
			.pipe(
				debounceTime(debounceDelay),
				distinctUntilChanged(),
				tap(license => this.facade.updateLicense({ license: license ?? '' })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToStoreChanges(): void {
		this.license$
			.pipe(
				tap(license => this.licenseFormControl.patchValue(license ?? null, { emitEvent: false })),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}
}
